import * as React from 'react';
import { type HTMLProps } from 'react';

import classnames from 'classnames';

import { Text } from '../../../Typography';
import { type StepperStatus, type StepperType } from '../../Stepper';
import { StepIcon } from '../StepIcon/StepIcon';
import { StepTail } from '../StepTail/StepTail';

import styles from './Step.module.scss';

export type StepProps = {
    className?: string;
    style?: React.CSSProperties;
    active?: boolean;
    disabled?: boolean;
    stepIndex: number;
    stepNumber: number;
    title?: React.ReactNode;
    icon?: React.ReactNode;
    onClick?: React.MouseEventHandler<HTMLDivElement>;
    onStepClick?: (index: number) => void;
    type?: StepperType;
    status?: StepperStatus;
    last?: boolean;
};

export const Step = ({
    className,
    active,
    status = 'wait',
    icon,
    stepNumber,
    disabled,
    title,
    stepIndex,
    onStepClick,
    onClick,
    type = 'number',
    last,
    ...rest
}: StepProps) => {
    const isClickable = Boolean(onStepClick) && !disabled;
    const clickableProps: HTMLProps<HTMLDivElement> = {};
    if (isClickable) {
        clickableProps.role = 'button';
        clickableProps.tabIndex = 0;
        clickableProps.onClick = (e) => {
            onClick?.(e);
            onStepClick?.(stepIndex);
        };
        clickableProps.onKeyDown = (e) => {
            const { code } = e;
            if (code === 'Enter' || code === 'Space') {
                onStepClick?.(stepIndex);
            }
        };
    }

    return (
        <div
            {...rest}
            className={classnames(styles.step, styles[type], styles[status], className, {
                [styles.active]: active,
                [styles.disabled]: disabled
            })}
        >
            <div onClick={onClick} {...clickableProps} className={styles.container}>
                {(!last || type === 'line') && (
                    <StepTail
                        type={type}
                        active={status === 'finish' || (type === 'line' && active)}
                    />
                )}
                {type !== 'line' && (
                    <StepIcon
                        status={status}
                        icon={icon}
                        active={active}
                        stepNumber={stepNumber}
                        type={type}
                    />
                )}
                <div className={styles.content}>
                    {type !== 'line' && (
                        <Text type={active ? 'h4' : 'text'} className={styles.title}>
                            {title}
                        </Text>
                    )}
                </div>
            </div>
        </div>
    );
};
